import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import Logo from './Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { currency, setCurrency, currencies } = useCurrency();

  const navLinks = [
    { name: 'Properties', href: '/properties' },
    { name: 'Visa Programs', href: '/visas' },
    { name: 'Destinations', href: '/destinations' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-3"
            data-testid="logo-link"
          >
            <Logo size="default" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-medium transition-colors border-gold-animated pb-1 ${
                  isActive(link.href)
                    ? 'text-[#D4AF37]'
                    : 'text-white/80 hover:text-white'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}

            {/* Currency Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="text-white/80 hover:text-white hover:bg-white/10"
                  data-testid="currency-selector"
                >
                  {currency}
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="bg-[#1A1D24] border-[#2A2F3A] text-white min-w-[100px]"
                data-testid="currency-dropdown"
              >
                {currencies.map((cur) => (
                  <DropdownMenuItem
                    key={cur}
                    onClick={() => setCurrency(cur)}
                    className={`cursor-pointer hover:bg-[#2A2F3A] focus:bg-[#2A2F3A] ${
                      currency === cur ? 'text-[#D4AF37]' : ''
                    }`}
                    data-testid={`currency-option-${cur.toLowerCase()}`}
                  >
                    {cur}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA Button */}
            <Link to="/contact">
              <Button 
                className="btn-luxury px-6"
                data-testid="nav-enquire-btn"
              >
                Enquire Now
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-[#0B0C10]/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => setIsOpen(false)}
                className={`block text-lg font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-[#D4AF37]'
                    : 'text-white/80 hover:text-white'
                }`}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile Currency Selector */}
            <div className="flex items-center space-x-2 py-2">
              <span className="text-white/60 text-sm">Currency:</span>
              {currencies.map((cur) => (
                <button
                  key={cur}
                  onClick={() => setCurrency(cur)}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    currency === cur
                      ? 'bg-[#D4AF37] text-[#0B0C10]'
                      : 'bg-[#1A1D24] text-white/80 hover:bg-[#2A2F3A]'
                  }`}
                  data-testid={`mobile-currency-${cur.toLowerCase()}`}
                >
                  {cur}
                </button>
              ))}
            </div>
            
            <Link to="/contact" onClick={() => setIsOpen(false)}>
              <Button 
                className="w-full btn-luxury"
                data-testid="mobile-enquire-btn"
              >
                Enquire Now
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
